<template>
  <div
    class="journey-sidebar"
    :class="{ collapsed, peeking: collapsed && peeking }"
    :style="(!collapsed || peeking) ? { width: width + 'px' } : null"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click="onPanelClick"
  >
    <div class="js-head">
      <span v-if="!collapsed || peeking" class="js-title">Journey</span>
      <button class="js-toggle" :title="collapsed ? 'Expand' : 'Collapse'" @click="collapsed = !collapsed">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
    </div>

    <div v-if="!collapsed || peeking" class="js-body">
      <div class="js-dropdown" :class="{ open: ddOpen }">
        <button class="js-dd-btn" @click="ddOpen = !ddOpen">
          <span class="js-dd-current">{{ currentJourneyKey || 'No journey' }}</span>
          <span v-if="versions.length" class="js-dd-version">v{{ versions[0].versionNumber }}</span>
          <svg class="js-dd-caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        <div v-if="ddOpen" class="js-dd-menu">
          <div class="js-dd-search">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/></svg>
            <input v-model="ddFilter" type="text" placeholder="Search journeys..." />
          </div>
          <div class="js-dd-list">
            <div
              v-for="k in filteredKeys"
              :key="k"
              class="js-dd-opt"
              :class="{ active: k === currentJourneyKey }"
              @click="selectJourney(k)"
            >
              <span class="js-dd-opt-name">{{ k }}</span>
              <svg v-if="k === currentJourneyKey" class="js-dd-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div v-if="!filteredKeys.length" class="js-dd-empty">No matches</div>
          </div>
        </div>
      </div>

      <button class="js-new-btn" @click="$emit('create-new')">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Create New
      </button>

      <journey-version-list
        :versions="versions"
        :active-version-number="activeVersionNumber"
        @select="$emit('select-version', $event)"
      />
    </div>

    <signed-in-user-panel v-if="!collapsed || peeking" />
  </div>
</template>

<script>
import JourneyVersionList from './JourneyVersionList.vue'
import SignedInUserPanel from './SignedInUserPanel.vue'

export default {
  name: 'journey-sidebar',
  components: { JourneyVersionList, SignedInUserPanel },
  props: {
    journeyKeys: { type: Array, default: () => [] },
    currentJourneyKey: { type: String, default: '' },
    versions: { type: Array, default: () => [] }
  },
  data () {
    return {
      collapsed: true,
      peeking: false,
      width: 240,
      ddOpen: false,
      ddFilter: ''
    }
  },
  computed: {
    filteredKeys () {
      const q = this.ddFilter.trim().toLowerCase()
      if (!q) return this.journeyKeys
      return this.journeyKeys.filter(k => k.toLowerCase().includes(q))
    },
    isOpen () {
      return !this.collapsed || this.peeking
    }
  },
  watch: {
    isOpen (val) {
      this.$emit('toggle-open', val)
    }
  },
  mounted () {
    if (!this.currentJourneyKey) this.collapsed = false
    this.onDocClick = e => {
      if (!this.$el.contains(e.target)) {
        this.ddOpen = false
        if (!this.collapsed) this.collapsed = true
      }
    }
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    onMouseEnter () {
      if (this.collapsed) this.peeking = true
    },
    onMouseLeave () {
      this.peeking = false
      this.ddOpen = false
    },
    onPanelClick () {
      if (this.collapsed && this.peeking) {
        this.collapsed = false
        this.peeking = false
      }
    },
    selectJourney (k) {
      this.$emit('journey-change', k)
      this.ddOpen = false
    },
    setCollapsed (v) {
      this.collapsed = v
    }
  }
}
</script>

<style scoped>
.journey-sidebar{
  --card:#1E3A5F;--card-border:rgba(255,255,255,0.1);--card-label-bg:rgba(255,255,255,0.07);
  --text:#ffffff;--text-muted:rgba(255,255,255,0.55);--text-faint:rgba(255,255,255,0.4);
  --accent:#14B8A6;--accent-light:#6EE7DE;
  position:fixed;top:0;left:0;bottom:0;width:40px;
  background:var(--card);border-right:1px solid var(--card-border);
  box-shadow:4px 0 16px rgba(0,0,0,0.06);
  display:flex;flex-direction:column;overflow:hidden;z-index:900;
  transition:width .15s ease,box-shadow .15s ease;
}
.journey-sidebar.peeking{box-shadow:8px 0 28px rgba(0,0,0,0.16)}
.js-head{
  padding:10px 12px;border-bottom:1px solid var(--card-border);
  display:flex;align-items:center;gap:8px;flex-shrink:0;
}
.journey-sidebar.collapsed:not(.peeking) .js-head{justify-content:center;padding:10px 4px}
.js-title{font-size:11px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;flex:1}
.js-toggle{
  width:24px;height:24px;border-radius:6px;background:transparent;border:1px solid transparent;
  color:var(--text-muted);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.js-toggle:hover{background:var(--card-label-bg);color:var(--text);border-color:var(--card-border)}
.js-toggle svg{transition:transform .15s}
.journey-sidebar.collapsed .js-toggle svg{transform:rotate(180deg)}

.js-body{flex:1;overflow-y:auto;padding:14px}
.js-body::-webkit-scrollbar{width:6px}
.js-body::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:3px}

.js-dropdown{position:relative}
.js-dd-btn{
  width:100%;padding:9px 10px 9px 12px;background:var(--card-label-bg);
  border:1px solid var(--card-border);border-radius:9px;
  display:flex;align-items:center;gap:8px;cursor:pointer;color:var(--text);text-align:left;
}
.js-dropdown.open .js-dd-btn{border-color:var(--accent);box-shadow:0 0 0 3px rgba(20,184,166,0.22)}
.js-dd-current{flex:1;overflow:hidden;font-family:'JetBrains Mono',monospace;font-size:12.5px;font-weight:500;white-space:nowrap;text-overflow:ellipsis}
.js-dd-version{flex-shrink:0;font-size:10px;font-weight:700;color:rgba(255,255,255,0.75);background:rgba(255,255,255,0.12);padding:2px 6px;border-radius:999px}
.js-dd-caret{color:var(--text-muted);transition:transform .15s;flex-shrink:0}
.js-dropdown.open .js-dd-caret{transform:rotate(180deg)}
.js-dd-menu{
  /* This popup floats over the page, not the dark sidebar rail, so it stays on the
     light theme regardless of the sidebar's own dark palette above. */
  --card:#ffffff;--card-border:#CBD5DF;--card-label-bg:#F6F8FA;
  --text:#16233A;--text-muted:#5D6C7E;--text-faint:#94A2B1;
  --accent:#0D9488;--accent-light:#0B7A70;
  position:absolute;top:calc(100% + 6px);left:0;right:0;
  background:var(--card);border:1px solid var(--card-border);border-radius:9px;
  box-shadow:0 12px 32px rgba(0,0,0,0.16);z-index:10;max-height:280px;
  display:flex;flex-direction:column;overflow:hidden;
}
.js-dd-search{padding:8px;border-bottom:1px solid var(--card-border);position:relative;display:flex;align-items:center}
.js-dd-search svg{position:absolute;left:16px;color:var(--text-faint)}
.js-dd-search input{
  width:100%;padding:7px 10px 7px 26px;background:var(--card-label-bg);
  border:1px solid var(--card-border);border-radius:6px;font-size:12px;color:var(--text);
}
.js-dd-search input::placeholder{color:var(--text-faint)}
.js-dd-search input:focus{outline:none;border-color:var(--accent)}
.js-dd-list{overflow-y:auto;padding:6px}
.js-dd-opt{padding:8px 10px;border-radius:6px;cursor:pointer;display:flex;align-items:center;gap:8px;color:var(--text)}
.js-dd-opt:hover{background:var(--card-label-bg)}
.js-dd-opt.active{background:rgba(13,148,136,0.11)}
.js-dd-opt-name{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:'JetBrains Mono',monospace;font-size:12px}
.js-dd-opt.active .js-dd-opt-name{color:var(--accent-light);font-weight:600}
.js-dd-check{color:var(--accent);flex-shrink:0}
.js-dd-empty{padding:10px;text-align:center;color:var(--text-faint);font-size:12px}

.js-new-btn{
  width:100%;margin-top:10px;padding:9px 12px;
  background:transparent;border:1px dashed var(--card-border);border-radius:9px;
  color:var(--text-muted);cursor:pointer;font-family:inherit;
  font-size:12.5px;font-weight:600;
  display:flex;align-items:center;justify-content:center;gap:6px;transition:all .12s;
}
.js-new-btn:hover{border-color:var(--accent);color:var(--accent-light);background:rgba(20,184,166,0.1)}
</style>



// WEBPACK FOOTER //
// JourneySidebar.vue?48cbc565